import React, { useState, useRef, useEffect } from 'react';
import Button from '@/components/atoms/Button';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import BartenderOrderList from '@/components/organisms/BartenderOrderList';
import BartenderOrderHistoryList from '@/components/organisms/BartenderOrderHistoryList';
import BartenderQRScanner from '@/components/molecules/BartenderQRScanner/BartenderQRScanner';
import BartenderOrderWorkflow from '@/components/organisms/BartenderOrderWorkflow';
import { EventOut, EventVenueOut, OrderOut } from '@/types/api';
import { useAuth } from '@/contexts/AuthContext';
import { apiService } from '@/services/api';
import './BartenderDashboard.scss';
import { SearchableSelect } from '@/components/molecules/SearchableSelect';

const BartenderDashboard: React.FC = () => {
  const { t } = useTranslation();
  const { user, refreshUser } = useAuth();
  const location = useLocation();
  const orderListRef = useRef<HTMLDivElement>(null);
  const [currentEvent, setCurrentEvent] = useState<EventOut | null>(null);
  const [eventLoading, setEventLoading] = useState(true);
  const [venues, setVenues] = useState<EventVenueOut[]>([]);
  const [venuesLoading, setVenuesLoading] = useState(true);
  const [savingVenue, setSavingVenue] = useState(false);
  const [feedback, setFeedback] = useState<string | null>(null);
  const [workflowError, setWorkflowError] = useState<string | null>(null);
  const [activeOrder, setActiveOrder] = useState<OrderOut | null>(null);
  const [openingOrderId, setOpeningOrderId] = useState<number | null>(null);
  const [scannerOpen, setScannerOpen] = useState(false);
  const [scannerError, setScannerError] = useState<string | null>(null);
  const [queueRefreshToken, setQueueRefreshToken] = useState(0);
  const showHistory = location.pathname === '/barman/orders';


  // Fetch event information on mount
  useEffect(() => {
    const fetchEventInfo = async () => {
      if (!user?.event_id) {
        setEventLoading(false);
        return;
      }

      try {
        const eventData = await apiService.getEvent(user.event_id);
        setCurrentEvent(eventData);
      } catch (error) {
        console.error('Failed to fetch event information:', error);
      } finally {
        setEventLoading(false);
      }
    };

    fetchEventInfo();
  }, [user]);

  // Fetch venues for the event so the barman can pick which bar they're working at
  useEffect(() => {
    const fetchVenues = async () => {
      if (!currentEvent?.id) {
        setVenuesLoading(false);
        return;
      }
      try {
        const venuesData = await apiService.getEventVenues(currentEvent.id);
        setVenues(venuesData);
      } catch (error) {
        console.error('Failed to fetch event venues:', error);
      } finally {
        setVenuesLoading(false);
      }
    };

    fetchVenues();
  }, [currentEvent]);

  const handleVenueChange = async (value: string) => {
    const newVenueId = value === '' ? null : parseInt(value, 10);
    setSavingVenue(true);
    try {
      await apiService.setMyVenue(newVenueId);
      await refreshUser();
    } catch (error) {
      console.error('Failed to update current venue:', error);
    } finally {
      setSavingVenue(false);
    }
  };

  const errorMessage = (error: any) =>
    error?.response?.data?.detail || t('bartender.orders.updateError');

  const showWorkflowError = (message: string) => {
    setWorkflowError(message);
    setTimeout(() => setWorkflowError(null), 5000);
  };

  // Step 1 -> 2: tapping an order reserves it for this bartender and opens it
  const handleOrderSelect = async (order: OrderOut) => {
    setOpeningOrderId(order.id);
    setWorkflowError(null);
    try {
      setActiveOrder(await apiService.claimOrder(order.id));
    } catch (error) {
      console.error('Failed to open order:', error);
      showWorkflowError(errorMessage(error));
      setQueueRefreshToken((n) => n + 1);
    } finally {
      setOpeningOrderId(null);
    }
  };

  // A code typed by a hardware QR/barcode reader into the search box. The
  // scan endpoint reserves the order too, so it opens straight in step 2.
  const handleCodeEntered = async (code: string) => {
    setWorkflowError(null);
    try {
      setActiveOrder(await apiService.scanOrderByQr(code));
    } catch (error: any) {
      console.error('Failed to open scanned order:', error);
      showWorkflowError(
        error?.response?.status === 404 ? t('bartender.qr.orderNotFound') : errorMessage(error)
      );
    }
  };

  // Each opening of the scanner starts clean: no error left over from the
  // previous session (#177).
  const openScanner = () => {
    setScannerError(null);
    setScannerOpen(true);
  };

  const closeScanner = () => {
    setScannerOpen(false);
    setScannerError(null);
  };

  const handleOrderScanned = (order: OrderOut) => {
    setScannerOpen(false);
    setScannerError(null);
    setActiveOrder(order);
  };

  const handleDelivered = (order: OrderOut) => {
    setActiveOrder(null);
    setQueueRefreshToken((n) => n + 1);
    setFeedback(t('bartender.workflow.delivered', { orderId: order.id }));
    setTimeout(() => setFeedback(null), 4000);
  };

  const handleWorkflowClose = () => {
    setActiveOrder(null);
    setQueueRefreshToken((n) => n + 1);
  };

  const activeOrderVenueName = activeOrder?.event_venue_id
    ? venues.find(v => v.id === activeOrder.event_venue_id)?.name || null
    : t('bartender.orderDetail.pista', { defaultValue: 'Pista' });

  // Scroll to order list when accessing /barman/orders on mobile
  useEffect(() => {
    if (location.pathname === '/barman/orders' && orderListRef.current) {
      // Check if we're on mobile (max-width: 1024px based on SCSS)
      const isMobile = window.matchMedia('(max-width: 1024px)').matches;
      
      if (isMobile) {
        // Add a small delay to ensure the page is rendered
        setTimeout(() => {
          orderListRef.current?.scrollIntoView({ 
            behavior: 'smooth', 
            block: 'start' 
          });
        }, 100);
      }
    }
  }, [location.pathname]);

  return (
    <div className="bartender-dashboard">
      <div className="bartender-dashboard__content">
        <div className="bartender-dashboard__main" ref={orderListRef}>
          
          {!eventLoading && !currentEvent && user && (
            <div className="bartender-dashboard__event-warning">
              ⚠️ {t('bartender.dashboard.noEventAssigned')}
            </div>
          )}

          {currentEvent && !venuesLoading && !showHistory && !activeOrder && (
            <div className="bartender-dashboard__venue-selector">
              <SearchableSelect<number>
                id="bartender-venue-select"
                className="bartender-dashboard__venue-select"
                label={t('bartender.dashboard.currentVenueLabel') || 'Estás en:'}
                placeholder={t('bartender.dashboard.noVenueSelected') || '— Elige tu barra —'}
                clearable
                options={venues.map((venue) => ({ value: venue.id, label: venue.name }))}
                value={user?.event_venue_id ?? null}
                onChange={(value) => handleVenueChange(value === null ? '' : String(value))}
                disabled={savingVenue}
              />
            </div>
          )}

          {currentEvent && !showHistory && !venuesLoading && !user?.event_venue_id && (
            <div className="bartender-dashboard__event-warning">
              {t('bartender.dashboard.selectVenueToWork')}
            </div>
          )}

          {feedback && <div className="bartender-dashboard__feedback">{feedback}</div>}
          {workflowError && <div className="bartender-dashboard__workflow-error">{workflowError}</div>}

          {showHistory ? (
            <BartenderOrderHistoryList
              eventId={currentEvent?.id}
              eventName={currentEvent?.name}
              eventVenueId={user?.event_venue_id ?? undefined}
              className="bartender-dashboard__order-list"
            />
          ) : activeOrder ? (
            <BartenderOrderWorkflow
              order={activeOrder}
              venueName={activeOrderVenueName}
              onDelivered={handleDelivered}
              onClose={handleWorkflowClose}
            />
          ) : (
            <BartenderOrderList
              eventId={currentEvent?.id}
              refreshToken={queueRefreshToken}
              openingOrderId={openingOrderId}
              onOrderSelect={handleOrderSelect}
              onCodeEntered={handleCodeEntered}
              onScanClick={openScanner}
              className="bartender-dashboard__order-list"
            />
          )}

          {scannerOpen && (
            <div className="bartender-dashboard__scanner-overlay" onClick={closeScanner}>
              <div className="bartender-dashboard__scanner-modal" onClick={(e) => e.stopPropagation()}>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={closeScanner}
                  className="bartender-dashboard__scanner-close"
                >
                  {t('bartender.orderDetail.close')}
                </Button>
                <BartenderQRScanner onOrderScanned={handleOrderScanned} onError={setScannerError} />
                {scannerError && <div className="bartender-dashboard__workflow-error">{scannerError}</div>}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default BartenderDashboard;